import React, { useState } from 'react';
import { Button, Typography, TextField, MenuItem, Box, Chip, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Build as CalculateIcon, Clear as ClearIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { updateUserProgress } from '../../../../services/apiService';

const COURSE_TYPES = [
  { value: 'data-science', label: 'Data Science' },
  { value: 'web3', label: 'Web3' },
];

/**
 * Bulk User Progress Calculation Component
 * Allows admins to calculate progress for multiple users at once
 */
export default function BulkProgressCalculation() {
  const [emailInput, setEmailInput] = useState('');
  const [courseType, setCourseType] = useState('dataScience');
  const [parsedEmails, setParsedEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Parse emails from comma-separated input
  const handleParseEmails = () => {
    if (!emailInput.trim()) {
      toast.error('Please enter at least one email address');
      return;
    }

    const emails = emailInput
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      toast.error(`Invalid email format: ${invalidEmails.join(', ')}`);
      return;
    }

    if (emails.length === 0) {
      toast.error('No valid emails found');
      return;
    }

    setParsedEmails(emails);
    setError(null);
    toast.success(`Parsed ${emails.length} email address(es)`);
  };

  // Clear all data
  const handleClear = () => {
    setEmailInput('');
    setParsedEmails([]);
    setResults(null);
    setError(null);
  };

  // Submit bulk progress calculation
  const handleSubmit = async () => {
    if (parsedEmails.length === 0) {
      toast.error('Please parse emails first');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      // Use the API service which sends data in body with emails as array
      const response = await updateUserProgress(courseType, parsedEmails);

      setResults({
        success: true,
        data: response,
        totalUsers: parsedEmails.length,
      });

      toast.success('Progress calculation completed');
    } catch (err) {
      console.error('Bulk progress calculation error:', err);
      
      setResults({
        success: false,
        error: err.response?.data?.message || err.message || 'Failed to calculate progress',
        totalUsers: parsedEmails.length,
      });

      toast.error('Failed to calculate progress');
    } finally {
      setLoading(false);
    }
  };

  const hasEmails = parsedEmails.length > 0;

  return (
    <div className='w-full pt-6'>
      <div className='bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden'>
        {/* Header */}
        <div className='p-6 border-b border-gray-100'>
          <Typography variant='h5' className='font-bold text-gray-900'>
            Bulk User Progress Calculation
          </Typography>
          <Typography variant='body2' className='text-gray-500 mt-2'>
            Calculate course progress for multiple users at once
          </Typography>
        </div>

        <div className='p-6 space-y-6'>
          {/* Course Type Selection */}
          <div>
            <Typography className='font-semibold mb-2'>
              Select Course Type
            </Typography>
            <TextField
              select
              fullWidth
              value={courseType}
              onChange={(e) => setCourseType(e.target.value)}
              variant='outlined'
              size='small'
            >
              {COURSE_TYPES.map((course) => (
                <MenuItem key={course.value} value={course.value}>
                  {course.label}
                </MenuItem>
              ))}
            </TextField>
          </div>

          {/* Email Input */}
          <div>
            <Typography className='font-semibold mb-2'>
              Enter Email Addresses
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder='user1@example.com, user2@example.com, user3@example.com'
              variant='outlined'
              size='small'
              helperText='Enter email addresses separated by commas'
            />
            <Button
              variant='contained'
              color='primary'
              onClick={handleParseEmails}
              className='mt-3'
              disabled={loading}
            >
              Parse Emails
            </Button>
          </div>

          {/* Preview Section */}
          {hasEmails && (
            <div className='border border-gray-200 rounded-xl p-4 bg-gray-50'>
              <div className='flex items-center justify-between mb-3'>
                <Typography className='font-semibold'>
                  Parsed Emails ({parsedEmails.length})
                </Typography>
                <Button
                  size='small'
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear All
                </Button>
              </div>
              <div className='flex flex-wrap gap-2'>
                {parsedEmails.map((email, index) => (
                  <Chip
                    key={index}
                    label={email}
                    size='small'
                    className='bg-white border border-gray-300'
                  />
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button
            fullWidth
            variant='contained'
            color='primary'
            size='large'
            startIcon={loading ? <CircularProgress size={20} color='inherit' /> : <CalculateIcon />}
            onClick={handleSubmit}
            disabled={loading || !hasEmails}
          >
            {loading
              ? `Calculating Progress...`
              : `Calculate Progress for ${parsedEmails.length} User(s)`}
          </Button>

          {/* Error Display */}
          {error && (
            <Alert severity='error' className='mt-4'>
              {error}
            </Alert>
          )}

          {/* Results Display */}
          {results && (
            <div className='mt-6'>
              {results.success ? (
                <Alert
                  severity='success'
                  icon={<CheckCircleIcon />}
                  className='mb-4'
                >
                  <Typography className='font-semibold mb-1'>
                    Calculation Completed
                  </Typography>
                  <Typography variant='body2'>
                    Successfully processed {results.totalUsers} user(s)
                  </Typography>
                </Alert>
              ) : (
                <Alert
                  severity='error'
                  icon={<ErrorIcon />}
                  className='mb-4'
                >
                  <Typography className='font-semibold mb-1'>
                    Calculation Failed
                  </Typography>
                  <Typography variant='body2'>
                    {results.error}
                  </Typography>
                </Alert>
              )}

              {/* Results Data */}
              {results.data && (
                <Box border={1} borderColor='#e5e7eb' borderRadius={8} overflow='hidden'>
                  <Box px={3} py={2} bgcolor='#f9fafb'>
                    <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
                      Results Details
                    </Typography>
                  </Box>
                  <Box p={3}>
                    <pre className='text-sm bg-gray-50 p-4 rounded overflow-auto max-h-96'>
                      {JSON.stringify(results.data, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
